/**
 * ViewModeToggle — three-button pill (Auto · Mobile · Desktop) that flips the
 * global rendering mode set by ViewModeContext. Persists immediately. Tiny
 * footprint so it can be embedded in dashboard headers without disrupting
 * existing layouts.
 *
 * Locked 2026-05-30 — Browser-vs-PWA awareness:
 *   "If you download it as an app that's when it allows you to switch between
 *    the two and it should be seamless switching between the two."
 *   In the installed PWA: toggle works silently as before.
 *   In a browser tab: tapping Desktop also shows a small one-time hint
 *   pointing users to "Request Desktop Site" or installing the app.
 */
import React, { useState } from 'react';
import { Smartphone, Monitor, Wand2, X } from 'lucide-react';
import { useViewMode } from '@/contexts/ViewModeContext';
import useIsStandalone from '@/hooks/useIsStandalone';

const HINT_DISMISSED_KEY = 'view_mode_desktop_browser_hint_dismissed';

const Btn = ({ active, onClick, label, Icon, testId, compact }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    aria-label={label}
    title={label}
    data-testid={testId}
    className={
      'flex items-center gap-1.5 text-xs font-medium rounded-full transition-colors ' +
      (compact ? 'px-1.5 py-1 ' : 'px-2.5 py-1 ') +
      (active
        ? 'bg-slate-900 text-white shadow-sm'
        : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100')
    }
  >
    <Icon className="h-3.5 w-3.5" />
    {!compact && <span className="leading-none">{label}</span>}
  </button>
);

const ViewModeToggle = ({ className = '', compact = false }) => {
  const { mode, setMode } = useViewMode();
  const isStandalone = useIsStandalone();
  const [hintOpen, setHintOpen] = useState(false);

  const handleDesktop = () => {
    setMode('desktop');
    // In browser (not PWA), show a one-time hint the first time the user
    // picks Desktop. After dismissal we don't show it again on this device.
    if (!isStandalone) {
      try {
        if (!localStorage.getItem(HINT_DISMISSED_KEY)) setHintOpen(true);
      } catch { /* localStorage blocked — silent */ }
    }
  };

  const dismissHint = () => {
    setHintOpen(false);
    try { localStorage.setItem(HINT_DISMISSED_KEY, '1'); } catch { /* ignore */ }
  };

  return (
    <div className="relative inline-flex flex-col items-end">
      <div
        className={
          'inline-flex items-center gap-0.5 p-0.5 bg-white/80 backdrop-blur-sm rounded-full border border-slate-200 shadow-sm ' +
          className
        }
        role="group"
        aria-label="View mode"
        data-testid="view-mode-toggle"
      >
        <Btn
          active={mode === 'auto'}
          onClick={() => setMode('auto')}
          label="Auto"
          Icon={Wand2}
          testId="view-mode-auto"
          compact={compact}
        />
        <Btn
          active={mode === 'mobile'}
          onClick={() => setMode('mobile')}
          label="Mobile"
          Icon={Smartphone}
          testId="view-mode-mobile"
          compact={compact}
        />
        <Btn
          active={mode === 'desktop'}
          onClick={handleDesktop}
          label="Desktop"
          Icon={Monitor}
          testId="view-mode-desktop"
          compact={compact}
        />
      </div>

      {hintOpen && (
        <div
          className="absolute top-full right-0 mt-2 w-72 z-50 bg-slate-900 text-white text-xs rounded-xl shadow-2xl p-3 pr-8 leading-relaxed"
          role="dialog"
          data-testid="view-mode-browser-hint"
        >
          <button
            type="button"
            onClick={dismissHint}
            data-testid="view-mode-browser-hint-close"
            className="absolute top-2 right-2 p-1 rounded-md hover:bg-white/10"
            aria-label="Dismiss hint"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <p className="font-semibold mb-1">Desktop view active</p>
          <p className="text-white/80">
            For the smoothest desktop experience, install RemodDash as an app
            or use your browser's <span className="font-semibold">Request Desktop Site</span> option.
          </p>
        </div>
      )}
    </div>
  );
};

export default ViewModeToggle;

